/**
 * Analytics consent gate — the single place any analytics may ever enter.
 *
 * Contract ("intelligent without surveillance"):
 *   - Nothing loads before the visitor makes an explicit choice.
 *   - The behavioral layer (intent inference, hints, adaptation) is local to
 *     the browser session and is NOT analytics — it never reports anywhere.
 *   - As of now NO analytics vendor is wired at all: `loadAnalytics` is a
 *     documented no-op scaffold. When an analytics ID exists, its loader goes
 *     into the marked injection points below and nowhere else.
 */

export interface ConsentState {
  /** Product analytics (page views, aggregated usage). */
  analytics: boolean;
  /** Marketing / advertising tags. The laboratory currently runs none. */
  marketing: boolean;
  /** Unix ms timestamp of when the choice was made. */
  ts: number;
}

/** localStorage key holding the consent choice as JSON. */
export const CONSENT_STORAGE_KEY = 'slp-consent';

/** Window event dispatched when consent is cleared (reopens the banner). */
export const CONSENT_RESET_EVENT = 'slp:consent-reset';

/** Read the stored consent choice, or null if none / malformed / unavailable. */
export function getConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState> | null;
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof parsed.analytics === 'boolean' &&
      typeof parsed.marketing === 'boolean' &&
      typeof parsed.ts === 'number'
    ) {
      return { analytics: parsed.analytics, marketing: parsed.marketing, ts: parsed.ts };
    }
    return null;
  } catch {
    return null;
  }
}

/** Persist a consent choice. Safe no-op when storage is unavailable. */
export function setConsent(c: ConsentState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(c));
  } catch {
    // storage blocked — the choice simply won't persist across loads
  }
}

/**
 * Withdraw consent: remove the stored choice and notify the consent banner to
 * reopen. Anything already loaded in this page cannot be unloaded — but since
 * no analytics are wired yet, in practice there is nothing to unload; once
 * there is, withdrawal prevents loading from the next page view on.
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // ignore — nothing was stored anyway
  }
  try {
    window.dispatchEvent(new Event(CONSENT_RESET_EVENT));
  } catch {
    // ignore
  }
}

let alreadyLoaded = false;

/**
 * Load analytics according to the visitor's consent. Currently a documented
 * no-op: no GA4 / Yandex.Metrica / any other IDs exist for this site yet.
 * This function is still called on every load (with the stored choice) and on
 * every fresh choice, so wiring a vendor later is a change to this file only.
 */
export function loadAnalytics(consent: ConsentState): void {
  if (typeof window === 'undefined') return;
  if (alreadyLoaded) return;
  if (!consent.analytics && !consent.marketing) return;
  alreadyLoaded = true;

  if (consent.analytics) {
    // ── Injection point: product analytics ──────────────────────────────
    // e.g. GA4:      inject gtag.js with the measurement ID here.
    // e.g. Metrica:  inject tag.js with the counter ID here.
    // Must stay strictly inside this `consent.analytics` guard.
  }

  if (consent.marketing) {
    // ── Injection point: marketing tags ─────────────────────────────────
    // The laboratory currently runs no advertising; if that ever changes,
    // pixels go here, strictly inside this `consent.marketing` guard.
  }
}
